import React, { useEffect } from "react";
import { CircleMarker, MapContainer, Polygon, Tooltip, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

type Place = { placeName?: string; lat: number; lng: number };
type LoomPin = {
  id?: string;
  loomCode?: string;
  lat: number;
  lng: number;
  insideGi?: boolean;
  distanceLabel?: string;
};

type Props = {
  places?: Place[];
  liveHull?: any;
  proposedHull?: any;
  hint?: string;
  looms?: LoomPin[];
  draftPin?: { lat: number; lng: number } | null;
};

const INDIA_CENTER: [number, number] = [22.5, 82];

const polygonFromHull = (hull: any): any => {
  if (!hull) {
    return null;
  }
  if (hull.type === "Feature") {
    return hull.geometry;
  }
  if (hull.type === "FeatureCollection") {
    return hull.features?.[0]?.geometry || null;
  }
  return hull;
};

const ringFromHull = (hull: any): [number, number][] => {
  const coords = polygonFromHull(hull)?.coordinates?.[0];
  if (!Array.isArray(coords)) return [];
  return coords
    .map((pair: any) => {
      if (!Array.isArray(pair) || pair.length < 2) return null;
      return [Number(pair[1]), Number(pair[0])] as [number, number];
    })
    .filter(Boolean) as [number, number][];
};

const FitBounds = ({ liveHull, proposedHull, places, looms, draftPin }: Props) => {
  const map = useMap();
  useEffect(() => {
    const latLngs: [number, number][] = [
      ...ringFromHull(liveHull),
      ...ringFromHull(proposedHull),
      ...(places || [])
        .filter((p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)))
        .map((p) => [Number(p.lat), Number(p.lng)] as [number, number]),
      ...(looms || [])
        .filter((p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)))
        .map((p) => [Number(p.lat), Number(p.lng)] as [number, number]),
    ];
    if (draftPin && Number.isFinite(draftPin.lat) && Number.isFinite(draftPin.lng)) {
      latLngs.push([draftPin.lat, draftPin.lng]);
    }
    if (latLngs.length === 0) {
      map.setView(INDIA_CENTER, 5);
      return;
    }
    map.fitBounds(latLngs, { padding: [24, 24], maxZoom: 12 });
  }, [map, liveHull, proposedHull, places, looms, draftPin]);
  return null;
};

const GiFenceMap = ({
  places = [],
  liveHull,
  proposedHull,
  looms = [],
  draftPin = null,
  hint = "Approximate fence from district points, not the legal GI map.",
}: Props) => {
  const liveRing = ringFromHull(liveHull);
  const proposedRing = ringFromHull(proposedHull);
  return (
    <div className="gi-fence-map">
      <MapContainer
        center={INDIA_CENTER}
        zoom={5}
        scrollWheelZoom
        className="gi-fence-map__canvas"
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds
          places={places}
          liveHull={liveHull}
          proposedHull={proposedHull}
          looms={looms}
          draftPin={draftPin}
        />
        {liveRing.length > 2 && (
          <Polygon
            positions={liveRing}
            pathOptions={{ color: "#1d4f7a", weight: 2, fillOpacity: 0.12 }}
          />
        )}
        {proposedRing.length > 2 && (
          <Polygon
            positions={proposedRing}
            pathOptions={{ color: "#b45309", weight: 2, dashArray: "8 6", fillOpacity: 0.06 }}
          />
        )}
        {places
          .filter((p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)))
          .map((p, i) => (
            <CircleMarker
              key={`place-${p.placeName}-${i}`}
              center={[Number(p.lat), Number(p.lng)]}
              radius={7}
              pathOptions={{ color: "#1d4f7a", fillColor: "#1d4f7a", fillOpacity: 0.9 }}
            >
              {p.placeName ? <Tooltip>{p.placeName}</Tooltip> : null}
            </CircleMarker>
          ))}
        {looms
          .filter((p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)))
          .map((p, i) => (
            <CircleMarker
              key={`loom-${p.id || p.loomCode}-${i}`}
              center={[Number(p.lat), Number(p.lng)]}
              radius={8}
              pathOptions={{
                color: p.insideGi === false ? "#b45309" : "#0f5132",
                fillColor: p.insideGi === false ? "#b45309" : "#0f5132",
                fillOpacity: 0.9,
              }}
            >
              <Tooltip>
                {p.loomCode || "Loom"}
                {p.distanceLabel ? ` — ${p.distanceLabel}` : ""}
              </Tooltip>
            </CircleMarker>
          ))}
        {draftPin && Number.isFinite(draftPin.lat) && Number.isFinite(draftPin.lng) ? (
          <CircleMarker
            center={[draftPin.lat, draftPin.lng]}
            radius={9}
            pathOptions={{ color: "#084298", fillColor: "#084298", fillOpacity: 1 }}
          >
            <Tooltip>This loom</Tooltip>
          </CircleMarker>
        ) : null}
      </MapContainer>
      <p className="gi-fence-map__hint">{hint}</p>
    </div>
  );
};

export default GiFenceMap;
